import React, { Dispatch, SetStateAction, useState } from 'react';
import { Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import { WindowInformation } from '../../../../services/window-information';
import { VacancyCancellationReasonsType } from '../../../../services/central-api/deac';
import { translate } from '../../../../services/translate';
import MyAppText from '../../../../components/MyAppText';
import CancellationReasonsDropdown from '../../../../components/deac/CancellationReasonsDropdown';
import CustomButton from '../../../../components/CustomButton';
import { appAgentService, CannotCancelApplication } from '../../../../services/central-api/app-agent';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    application: Application;
    onConfirm: () => void;
}

export default function ApplicationCancelModal({ application, isModalVisible, setModalVisible, onConfirm }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [cancellationReasonId, setCancellationReasonId] = useState<number>(0);

    async function formSubmit() {
        try {
            setIsLoading(true);
            await appAgentService.cancelApplication(application.id, cancellationReasonId);

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            onConfirm();
        } catch (err) {
            if (err instanceof CannotCancelApplication) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('cannotDeleteVacancy'),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
            closeModal();
        }
    }

    const closeModal = () => {
        setCancellationReasonId(0);
        setModalVisible(false);
    };

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => closeModal()}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    <View style={{ flexDirection: 'column', rowGap: 8 }}>
                        <MyAppText style={styles.header}>{translate('cancelApplication')}</MyAppText>
                        <MyAppText style={styles.subheader}>{translate('provideReasonBelow')}</MyAppText>
                    </View>
                    <CancellationReasonsDropdown
                        setValue={setCancellationReasonId}
                        value={cancellationReasonId}
                        purpose={VacancyCancellationReasonsType.APPLICATION_APP_AGENT}
                        zIndex={2}
                        labelStyle={styles.reasonLabelStyle}
                        viewStyle={{ gap: 8 }}
                    />
                    <View style={{ flexDirection: 'column', gap: 8 }}>
                        <CustomButton
                            onPress={formSubmit}
                            disabled={cancellationReasonId == 0 || isLoading}
                            style={{ paddingHorizontal: 16, paddingVertical: 9, }}
                            type='primary'
                            text={translate('cancelApplication')}
                        />
                        <CustomButton
                            onPress={closeModal}
                            style={{ paddingHorizontal: 16, paddingVertical: 9, }}
                            type='secondary'
                            text={translate('goBack')}
                        />
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
        borderRadius: 8
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : 450,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32
    }),
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 16,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    reasonLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    }
}));
